<script setup>
import { ref, onMounted, onUnmounted, watch, computed, reactive } from 'vue'
import { gApp } from '../State.js'
import { ProjectTypes } from '../Project.js'
import { waitMillis, } from '../SharedUtils.js'
import { AsyncLock, YesNoOrChecking, }  from '../Base.js'
import BasicModal from './utils/BasicModal.vue'
import ProjectsTable from './ProjectsTable.vue'
import InputGroup from './InputGroup.vue'
import RadioSelector from './RadioSelector.vue'
import UnitsSelector from './UnitsSelector.vue'
import { FreeTrialConfig } from '../Common/License.js'
import { getAuth } from 'firebase/auth'

let createProjModal = ref(null);
let welcomeModal = ref(null);
let licenseErrorModal = ref(null);
let importFileInput = ref(null);
let isImporting = ref(false);
let newProjName = ref("");
let newProjType = ref(ProjectTypes.Commercial);
let newProjUnits = ref("Imperial");
let createProjectLock = new AsyncLock();

let welcomeProjectName = ref("");
let welcomeProjectType = ref(ProjectTypes.Commercial);
let welcomeProjectUnits = ref("Imperial");

let debugProjects = [
  {id: '0'},
  {id: '1'},
  {id: '2'},
];

function startCreateProject() {
  // Check if license is active before allowing project creation
  if (license.value && !license.value.isActive()) {
    licenseErrorModal.value.showModal();
    return;
  }
  
  newProjName.value = "";
  newProjType.value = ProjectTypes.Commercial;
  newProjUnits.value = "Imperial";
  createProjModal.value.showModal();
}

async function createProject() {
  await createProjectLock.run(async () => {
    let projectName = newProjName.value || "Untitled";
    await gApp.createProject(projectName, newProjType.value, newProjUnits.value);
  });
}

function startImportProject() {
  importFileInput.value.click();
}

async function onImportFileSelected(event) {
  let file = event.target.files && event.target.files[0];
  // Reset the input so selecting the same file again re-triggers change.
  event.target.value = "";
  if (!file) {
    return;
  }
  if (isImporting.value) {
    return;
  }
  isImporting.value = true;
  try {
    let text = await file.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (err) {
      gApp.toastError(`Could not parse "${file.name}": not valid JSON.`);
      return;
    }
    let projId = await gApp.importProjectFromData(data);
    gApp.toast(`Imported "${file.name}"`);
    console.log(`Imported project as ${projId}`);
  } catch (err) {
    console.error("Error importing project:", err);
    gApp.toastError(`Error importing project: ${err.message}`);
  } finally {
    isImporting.value = false;
  }
}

async function sendEmailVerification() {
  await gApp.sendEmailVerification(true);
}

let myProjects = computed(() => {
  return gApp.projects.filter((proj) => { return proj.isMine; })
})

let sharedProjects = computed(() => {
  return gApp.projects.filter((proj) => { return !proj.isMine; })
})

const license = computed(() => gApp.getLicense())

function getCreateProjectOptions() {
  let options = [];
  let commercialOption = {
    value: 'Commercial',
    label: 'Commercial Building',
    info: 'Calculate loads for a commercial building project.'
  };
  let residentialOption = {
    value: 'Residential',
    label: 'House',
    info: 'Calculate loads for a residential house project.'
  };
  if (license.value) {
    if (license.value.commercialActive()) {
      options.push(commercialOption);
    }
    if (license.value.residentialActive()) {
      options.push(residentialOption);
    }
  } else {
    options = [commercialOption, residentialOption];
  }
  return options;
}

function getLicenseInfo() {
  if (!license.value) {
    return "";
  }
  if (!license.value.commercialActive()) {
    return "Note: Your license only allows house projects.";  
  }
  return "";
}


function showWelcomeModal() {
  welcomeProjectName.value = "";
  welcomeProjectType.value = ProjectTypes.Commercial;
  welcomeProjectUnits.value = "Imperial";
  welcomeModal.value.showModal();
}

async function createFirstProject() {
  // Check if license is active before allowing project creation
  if (license.value && !license.value.isActive()) {
    welcomeModal.value.closeModal();
    licenseErrorModal.value.showModal();
    return;
  }
  
  welcomeModal.value.closeModal();
  await createProjectLock.run(async () => {
    let projectName = welcomeProjectName.value || "Untitled";
    await gApp.createProject(projectName, welcomeProjectType.value, welcomeProjectUnits.value);
  });
}

function openUpgradeLink() {
  window.open('https://www.heatwise-hvac.com/pricing', '_blank');
}

function goToAccountSettings() {
  gApp.router.push('/account');
}

let isEmailVerified = computed(() => {
  return gApp.userInfo.emailVerified;
});

function logUser() {
  console.log("Current userInfo:", gApp.userInfo);
  console.log("Current auth user: ", gApp.auth.currentUser);
}

function setCurrentAppVersion() {
  gApp.currentAppVersion.value = "12345.123";
}

// Watch for gApp.projects changes
watch(gApp.userHasProjects, (newVal) => {
  if (newVal == YesNoOrChecking.No) {
    showWelcomeModal();
  } else if (newVal == YesNoOrChecking.Yes) {
    // Do nothing
    welcomeModal.value.closeModal();
  }
})

onMounted(() => {
  if (gApp.isLoggedIn()) {
    gApp.closeProject();
    gApp.reloadProjects(); 
  }
})
</script>

<template>
  <div class="Projects">
    <div class="TextAlignCenter">
      <h2 class="fw-normal2">Welcome to HeatWise.</h2>
      <p class="big-help-text mb-s">Create a new project to start calculating loads.</p>
      <button @click="startCreateProject" class="CreateProjBtn BigCreateBtn mlr-auto">Create Project</button>
    </div>
    <div v-if="!isEmailVerified" class="EmailVerification mt-l mb-ml">
      <div class="EmailVerificationContent">
        <div class="EmailVerificationIcon">
          <i class="bi-envelope-check"></i>
        </div>
        <div class="EmailVerificationInfo">
          <p class="EmailVerificationTitle">Verify Your Email</p>
          <p class="EmailVerificationText">We sent a verification email to <b>{{gApp.userInfo.email}}</b></p>
          <p class="EmailVerificationHelp">Don't see it? Please check your spam folder or 
            <button @click="sendEmailVerification" class="EmailVerificationResend">
              resend verification email
            </button>
          </p>
        </div>
      </div>
    </div>
    
    <!-- Free Trial Banner - Only show if not indefinite -->
    <div v-if="license && !license.isPaid() && license.expiresAt !== FreeTrialConfig.INDEFINITE" class="TrialBanner mt-l mb-s">
      <div class="TrialBannerContent">
        <div class="TrialIcon">
          <i class="bi-hourglass-split"></i>
        </div>
        <div class="TrialInfo">
          <h3 class="TrialTitle">Free Trial</h3>
          <div v-if="license.getDaysRemaining() > 0" class="TrialProgress">
            <div class="TrialProgressBar" :style="{ width: `${Math.min(100, (license.getDaysRemaining() / FreeTrialConfig.TRIAL_DAYS) * 100)}%` }"></div>
          </div>
          <p v-if="license.getDaysRemaining() > 0" class="TrialDaysLeft">
            {{ license.getDaysRemaining() }} days remaining
          </p>
          <p v-else class="TrialExpired">
            Your free trial has expired
          </p>
        </div>
        <button @click="openUpgradeLink" class="UpgradeBtn">
          <i class="bi-arrow-up-circle mr-xxs"></i>Upgrade Now
        </button>
      </div>
    </div>
    
    <!-- License Admin Button -->
    <div v-if="license && license.isMultiSeatLicense()" class="LicenseAdminSection mt-l mb-l">
      <div class="LicenseAdminContent">
        <div class="LicenseAdminIcon">
          <i class="bi-shield-check"></i>
        </div>
        <div class="LicenseAdminInfo">
          <h3 class="LicenseAdminTitle">Manage Your Licenses</h3>
          <p class="LicenseAdminText">You are the owner of a team license with <strong>{{ license.quantity }} seats</strong>. Manage seat assignments and view license activity here.</p>
        </div>
        <router-link to="/license-admin" class="LicenseAdminBtn">
          <i class="bi-gear mr-xxs"></i>Manage License
        </router-link>
      </div>
    </div>
    
    <div class="MarginBotM mt-xl">
      <div class="mb-xl">
        <div class="">
          <div class="table-header table-header-with-action">
            <h2 class="TableTitle">
              <i class="bi-folder-fill header-icon"></i>
              <span class="header-text">My Projects</span>
            </h2>
            <button @click="startImportProject" :disabled="isImporting" class="ImportBtn">
              <i class="bi-download mr-xxs"></i>{{ isImporting ? 'Importing...' : 'Import project' }}
            </button>
            <input
              ref="importFileInput"
              type="file"
              accept=".json,.hwise,application/json"
              style="display: none"
              @change="onImportFileSelected"
            />
          </div>
          <ProjectsTable class="projects-section" :projects="myProjects" />
        </div>
      </div>
      <div class="mb-xl">
        <div class="">
          <div class="table-header">
            <h2 class="TableTitle">
              <i class="bi-people-fill header-icon"></i>
              <span class="header-text">Shared with me</span>
            </h2>
          </div>
          <ProjectsTable class="projects-section" :projects="sharedProjects" />
        </div>
      </div>
      <div v-if="gApp.isAdminUser()" class="MarginBotM">
        <h2 class="mb-xxs">ADMIN Features</h2>
        <p>(This section is only visible to admin users)</p>
        <div>
          <button @click="setCurrentAppVersion">Set current app version</button>
        </div>
        <div>
          <router-link to="/psychrometrics" class="LinkBtn">Psychrometrics Calculator</router-link>
        </div>
        <div>
          <router-link to="/solar-calculator" class="LinkBtn">Solar Calculator</router-link>
        </div>
      </div>
    </div>
    
    <BasicModal ref="licenseErrorModal" title="License Required" doneText="Manage License" cancelText="Close" @onDone="goToAccountSettings">
      <div class="mb-m">
        <p class="mb-s">Your license has expired or is inactive. Go to account settings to manage your license.</p>
      </div>
    </BasicModal>
    
    <BasicModal ref="createProjModal" title="Create Project" doneText="Create" @onDone="createProject">
      <div class="">
        <p class="mute-header">Name</p>
        <input v-model="newProjName" class="BigInput mb-s" placeholder="Untitled" autofocus @keyup.enter="createProject" />
      </div>
      <div class="mb-m">
        <p class="mute-header mb-xxs">Building Type</p>
        <!--<p class="mb-s">Choose building type:</p>-->
        <div>
          <RadioSelector v-model="newProjType" :options="getCreateProjectOptions()" />
        </div>
        <div class="mt-s mb-m" v-if="getLicenseInfo()">
          <p class="help-text">
            {{ getLicenseInfo() }}
          </p>
        </div>
      </div>
      <div class="mb-m">
        <p class="mute-header">Units</p>
        <div>
          <UnitsSelector v-model="newProjUnits" />
        </div>
      </div>
    </BasicModal>
    
    <BasicModal ref="welcomeModal" :showCancel="false" :showDone="false">
      <div>
        <h2 class="TextAlignCenter pc mb-s welcome-heading">Welcome to HeatWise</h2>
        <p class="TextAlignCenter Bold mb-m">
          Let's create your first project.
        </p>
      </div>
      
      <div class="project-form">
        <div class="mb-s">
          <p class="mute-header">Project Name</p>
          <input v-model="welcomeProjectName" class="BigInput mb-s" placeholder="Untitled" autofocus @keyup.enter="createFirstProject" />
        </div>
        <div class="mb-s">
          <p class="mute-header mb-xxs">Building Type</p>
          <div>
            <RadioSelector v-model="welcomeProjectType" :options="getCreateProjectOptions()" />
          </div>
        </div>
        <div class="mb-s">
          <p class="mute-header mb-xxs">Units</p>
          <div>
            <UnitsSelector v-model="welcomeProjectUnits" />
          </div>
        </div>
      </div>
      <div class="welcome-buttons">
        <button @click="createFirstProject" class="MarginAuto CreateBtn BigCreateBtn">
          Create Project
        </button>
      </div>
    </BasicModal>
  </div>
</template>

<style scoped>
.Projects {
  margin: 0 auto;
  max-width: 1200px;
}

.CreateProjBtn {
  border-radius: var(--br-m);
  padding: var(--s) var(--m);
}

.EmailVerification {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  background-color: var(--pc-lightest);
  border-radius: var(--br-m);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid rgba(62, 85, 204, 0.2);
}

.EmailVerificationContent {
  display: flex;
  align-items: center;
  padding: var(--space-s);
}

.EmailVerificationIcon {
  font-size: 1.5rem;
  color: var(--pc);
  margin-right: var(--space-s);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background-color: rgba(62, 85, 204, 0.1);
  border-radius: 50%;
  flex-shrink: 0;
}

.EmailVerificationInfo {
  flex: 1;
}

.EmailVerificationTitle {
  font-size: var(--f-m);
  font-weight: 600;
  color: var(--pc-dark);
  margin-bottom: var(--space-xxs);
}

.EmailVerificationText {
  font-size: var(--f-s);
  margin-bottom: var(--space-xs);
  color: var(--grey-40);
}

.EmailVerificationHelp {
  font-size: var(--f-xs);
  color: var(--grey-50);
}

.EmailVerificationResend {
  color: var(--pc);
  font-weight: 600;
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline;
}

.EmailVerificationResend:hover {
  color: var(--pc-dark);
  background: none;
}

.BigInput {
  padding: 12px;
  font-size: var(--f-m);
  width: 80%;
  border: 1px solid var(--b-20);
}

.TableTitle {
  font-weight: 600;
  display: flex;
  align-items: center;
  position: relative;
  padding-left: var(--s);
  border-radius: var(--br-s) var(--br-s) 0 0;
  margin-bottom: 0;
}

.header-icon {
  color: var(--pc);
  margin-right: var(--xs);
  font-size: 1.2em;
}

.header-text {
  position: relative;
  z-index: 1;
  color: var(--text-dark);
  font-weight: 600;
  letter-spacing: 0.5px;
}

.header-accent {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  width: 100%;
  background: linear-gradient(90deg, var(--pc), rgba(62, 85, 204, 0.1));
}

.table-header {
  position: relative;
  border-radius: var(--br-m) var(--br-m) 0 0;
  background-color: var(--white);
}

.table-header-with-action {
  display: flex;
  align-items: center;
}

.table-header-with-action .TableTitle {
  margin-right: var(--s);
}

.ImportBtn {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  background: none;
  border: 1px solid var(--pc);
  border-radius: var(--br-s);
  padding: var(--xxs) var(--s);
  color: var(--pc);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ImportBtn:hover:not(:disabled) {
  color: var(--pc-dark);
  border-color: var(--pc-dark);
  background: none;
}

.ImportBtn:disabled {
  color: var(--grey-50);
  border-color: var(--grey-50);
  cursor: default;
}


.feature-list {
  text-align: left;
  padding: var(--m);
  background-color: var(--b-5);
  border-radius: var(--br-m);
}

.feature-item h4 {
  margin-bottom: var(--xxs);
  font-weight: 500;
}


.welcome-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: var(--l);
}

.primary-btn {
  background-color: var(--primary);
  color: white;
  padding: var(--s) var(--m);
  border-radius: var(--br-m);
  font-weight: 500;
  border: none;
  cursor: pointer;
}

.secondary-btn {
  background-color: var(--b-10);
  color: var(--text);
  padding: var(--s) var(--m);
  border-radius: var(--br-m);
  font-weight: 500;
  border: 1px solid var(--b-20);
  cursor: pointer;
}

.note-text {
  font-size: var(--f-s);
  color: var(--text-muted);
  font-style: italic;
}

.project-form {
  text-align: left;
  border-radius: var(--br-m);
}

.welcome-heading {
  /*animation: fadeIn 0.5s ease-in-out;*/
}
.projects-section {
  margin-bottom: var(--xl);
  box-shadow: 0px 8px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--b-10);
  /*box-shadow: 0 4px 12px black;*/
  border-radius: var(--br-m);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.BigCreateBtn {
  border-radius: var(--br-m);
  padding: var(--s) var(--m);
  font-size: var(--f-m);
  box-shadow: 0 4px 8px rgba(62, 85, 204, 0.3);
  transition: all 0.2s ease;
}

.BigCreateBtn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(62, 85, 204, 0.4);
}

/* Free Trial Banner Styles */
.TrialBanner {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  background-color: var(--pc-lightest);
  border-radius: var(--br-m);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid rgba(62, 85, 204, 0.2);
}

.TrialBannerContent {
  display: flex;
  align-items: center;
  padding: var(--space-s);
}

.TrialIcon {
  font-size: 1.5rem;
  color: var(--pc);
  margin-right: var(--space-s);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background-color: rgba(62, 85, 204, 0.1);
  border-radius: 50%;
  flex-shrink: 0;
}

.TrialInfo {
  flex: 1;
}

.TrialTitle {
  font-size: var(--f-m);
  font-weight: 600;
  color: var(--pc-dark);
  margin-bottom: var(--space-xxs);
}

.TrialProgress {
  height: 8px;
  background-color: var(--grey-90);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: var(--space-xxs);
  width: 100%;
}

.TrialProgressBar {
  height: 100%;
  background-color: var(--pc);
  border-radius: 4px;
}

.TrialDaysLeft {
  font-size: var(--f-s);
  font-weight: 500;
  color: var(--pc-dark);
}

.TrialUnlimited {
  font-size: var(--f-s);
  font-weight: 600;
  color: #2ecc71;
}

.TrialExpired {
  font-size: var(--f-s);
  font-weight: 500;
  color: var(--delete-red);
}

.UpgradeBtn {
  background-color: var(--pc);
  color: white;
  border: none;
  border-radius: var(--br-s);
  padding: var(--space-xs) var(--space-m);
  font-weight: 600;
  white-space: nowrap;
  margin-left: var(--space-m);
  transition: all 0.2s ease;
}

.UpgradeBtn:hover {
  background-color: var(--pc-dark);
  transform: translateY(-2px);
}

/* License Admin Section Styles */
.LicenseAdminSection {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  background: linear-gradient(135deg, rgba(62, 85, 204, 0.05) 0%, rgba(62, 85, 204, 0.02) 100%);
  border-radius: var(--br-m);
  box-shadow: 0 4px 12px rgba(62, 85, 204, 0.15);
  overflow: hidden;
  border: 1px solid rgba(62, 85, 204, 0.2);
  position: relative;
}

.LicenseAdminSection::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--pc), rgba(62, 85, 204, 0.6));
}

.LicenseAdminContent {
  display: flex;
  align-items: center;
  padding: var(--space-m);
  gap: var(--space-s);
}

.LicenseAdminIcon {
  font-size: 2rem;
  color: var(--pc);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, rgba(62, 85, 204, 0.1) 0%, rgba(62, 85, 204, 0.05) 100%);
  border-radius: 50%;
  flex-shrink: 0;
  border: 2px solid rgba(62, 85, 204, 0.2);
}

.LicenseAdminInfo {
  flex: 1;
}

.LicenseAdminTitle {
  font-size: var(--f-l);
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: var(--space-xs);
  letter-spacing: -0.02em;
}

.LicenseAdminText {
  font-size: var(--f-s);
  color: var(--grey-30);
  line-height: 1.5;
}

.LicenseAdminBtn {
  background: linear-gradient(135deg, var(--pc) 0%, rgba(62, 85, 204, 0.9) 100%);
  color: white;
  border: none;
  border-radius: var(--br-s);
  padding: var(--space-s) var(--space-m);
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(62, 85, 204, 0.3);
  position: relative;
  overflow: hidden;
}

.LicenseAdminBtn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.LicenseAdminBtn:hover {
  background: linear-gradient(135deg, var(--pc-dark) 0%, var(--pc) 100%);
  color: white;
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(62, 85, 204, 0.4);
}

.LicenseAdminBtn:hover::before {
  left: 100%;
}
</style>
